import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api.jsx';
import {
  clearSession,
  getStoredUser,
  getToken,
  hasValidToken,
  onSessionChange,
  setSession,
  updateStoredUser,
} from './session';

const AuthContext = createContext(null);

const readSession = () => {
  if (!getToken()) {
    return { user: null, isAuthenticated: false };
  }
  if (!hasValidToken()) {
    clearSession();
    return { user: null, isAuthenticated: false };
  }
  return { user: getStoredUser(), isAuthenticated: true };
};

export const AuthProvider = ({ children }) => {
  const [session, setSessionState] = useState(readSession);

  useEffect(() => onSessionChange(() => setSessionState(readSession())), []);

  // The stored profile is only a cache; the server's copy decides the role.
  useEffect(() => {
    if (!hasValidToken()) return undefined;
    let cancelled = false;
    apiService
      .getCurrentUser()
      .then((response) => {
        if (!cancelled && response?.user) {
          updateStoredUser(response.user);
        }
      })
      .catch(() => {
        // 401s clear the session inside the API client; other failures keep the cached profile.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback((token, user) => setSession(token, user), []);
  const signOut = useCallback(() => clearSession(), []);
  const updateUser = useCallback((user) => updateStoredUser(user), []);

  const value = useMemo(
    () => ({
      user: session.user,
      isAuthenticated: session.isAuthenticated,
      isAdmin: session.user?.role === 'admin',
      signIn,
      signOut,
      updateUser,
    }),
    [session, signIn, signOut, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
};
